import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { LocalizedLink as Link } from "@/components/LocalizedLink";
import UnitStatus from "@/components/UnitStatus";
import FromPrice from "@/components/FromPrice";
import { Reveal } from "@/components/motion";
import { useLang } from "@/i18n";
import { UNITS } from "@/data/site";

// Bookable first, then coming soon, then not available; panel order within each.
const rank = (u) => (u.bookable ? 0 : u.availabilityStatus === "comingSoon" ? 1 : 2);
const ORDERED = UNITS.map((u, i) => [u, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]).map(([u]) => u);
const PER_VIEW = 3;
const SCROLLS = ORDERED.length > PER_VIEW;

function Card({ unit, lang, label }) {
  return (
    <Link to={unit.path} className="group flex flex-col h-full" data-testid={`home-unit-${unit.slug}`}>
      <div className="overflow-hidden rounded-2xl aspect-[4/3] bg-sand">
        {unit.cover && <img src={unit.cover} alt={unit.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
      </div>
      <p className="mt-5 text-xs uppercase tracking-[0.2em] text-stone">{unit.tagline[lang]}</p>
      <h3 className="font-display text-3xl md:text-4xl tracking-tight mt-1.5">{unit.name}</h3>
      <UnitStatus unit={unit} className="mt-2" />
      <FromPrice unit={unit} className="mt-1" />
      <p className="mt-3 text-stone font-light leading-relaxed max-w-md">{unit.teaser[lang]}</p>
      <span className="mt-auto pt-4 inline-flex items-center gap-2 text-sm text-terracotta">
        {label}
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
      </span>
    </Link>
  );
}

// Any number of apartments, one row that never shows the edge of a hidden card.
// Phones: one card at a time, swipe or arrows. Desktop: up to three fill the
// row with no arrows; more scroll by whole pages of three.
export default function HomeUnits() {
  const { t, lang } = useLang();
  const h = t.homeUnits;
  const track = useRef(null);
  const [edge, setEdge] = useState({ start: true, end: false });

  useEffect(() => {
    const el = track.current;
    if (!el) return undefined;
    const on = () => setEdge({ start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth > el.scrollWidth - 8 });
    on();
    el.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => { el.removeEventListener("scroll", on); window.removeEventListener("resize", on); };
  }, []);

  if (!ORDERED.length) return null;
  // One page = the visible width plus the gap after it (gap-8 = 32px on desktop, gap-4 on phones).
  const page = (dir) => {
    const el = track.current;
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    el.scrollBy({ left: dir * (el.clientWidth + gap), behavior: "smooth" });
  };
  const arrow = "inline-flex h-12 w-12 items-center justify-center rounded-full border transition-colors duration-200 disabled:opacity-35 disabled:cursor-default";

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-32" data-testid="home-units">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <Reveal>
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-terracotta">{h.overline}</p>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight mt-4">{h.title}</h2>
        </Reveal>
        <div className="ml-auto flex items-center gap-4">
          <Link to="/unita" className="hidden md:inline text-sm border-b border-ink pb-0.5 hover:text-terracotta hover:border-terracotta transition-colors duration-200">
            {h.all}{ORDERED.length > 1 ? ` (${ORDERED.length})` : ""}
          </Link>
          {ORDERED.length > 1 && (
            <div className={`flex items-center gap-4 ${SCROLLS ? "" : "md:hidden"}`}>
              <button type="button" onClick={() => page(-1)} disabled={edge.start} aria-label={h.prev} className={`${arrow} border-ink/20 text-ink hover:border-terracotta hover:text-terracotta`}>
                <ArrowLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <button type="button" onClick={() => page(1)} disabled={edge.end} aria-label={h.next} className={`${arrow} border-terracotta bg-terracotta text-cream hover:bg-terracotta-dark`}>
                <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        ref={track}
        className={`mt-10 md:mt-14 flex gap-4 md:gap-8 overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${SCROLLS ? "" : `md:grid md:overflow-visible ${ORDERED.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}`}
        data-testid="home-units-track"
      >
        {ORDERED.map((u) => (
          <div key={u.slug} className={`snap-start shrink-0 basis-full ${SCROLLS ? "md:basis-[calc((100%-4rem)/3)]" : ""}`}>
            <Card unit={u} lang={lang} label={h.discover} />
          </div>
        ))}
      </div>

      <Link to="/unita" className="md:hidden mt-10 flex justify-center rounded-full border border-ink/20 py-3.5 text-sm font-medium">
        {h.all}{ORDERED.length > 1 ? ` (${ORDERED.length})` : ""}
      </Link>
    </section>
  );
}
